import { useCallback, useEffect, useMemo, useState } from "react";
import { useWordContext } from "../context/WordContext";
import {
  PlatzBlobViewTokensItem,
  YoutubeTokenViewToken,
  WordView,
} from "../api-generated";
import { isWordToken } from "../utils";

//SNIPPET
//Array(n).fill(0)

type Token = PlatzBlobViewTokensItem | YoutubeTokenViewToken;

export type MultiSelectableBlob = Token[];

export interface BlobGroupLocation {
  blobGroupIndex: number;
  blobIndex: number;
  tokenIndex: number;
}

function useMultiSelect(
  blobGroups: MultiSelectableBlob[][],
  getEmptyMultiSelectState: () => number[][][]
): {
  multiSelectTokenIndeces: number[][][];
  multiSelectedWords: WordView[];
  isMultiSelectOn: boolean;
  setIsMultiSelectOn: (onOrOff: boolean) => void;
  closeMultiSelect: () => void;

  handleToggleMultiSelect: (location: {
    //NB: not using SourceLocation from api, since I don't
    //want any args to be optional, and atow I don't have a way
    //to enforce this with typing
    blobGroupIndex: number;
    tokenIndex: number;
    blobIndex: number;
  }) => void;
  removeWordFromMultiSelect: (wordId: number) => void;
} {
  const [isMultiSelectOn, setIsMultiSelectOn] = useState(false);

  const { setShouldIgnoreClicks } = useWordContext();
  useEffect(() => {
    setShouldIgnoreClicks(isMultiSelectOn);
  }, [isMultiSelectOn]);

  const refreshState = useCallback(() => {
    setMultiSelectTokenIndeces(getEmptyMultiSelectState());
  }, [getEmptyMultiSelectState]);

  useEffect(refreshState, [refreshState]);

  const closeMultiSelect = useCallback(() => {
    setIsMultiSelectOn(false);
    refreshState();
  }, [refreshState]);

  const [multiSelectTokenIndeces, setMultiSelectTokenIndeces] = useState<
    number[][][]
  >(getEmptyMultiSelectState());

  const handleToggleMultiSelect = useCallback(
    (location: {
      blobGroupIndex: number;
      tokenIndex: number;
      blobIndex: number;
    }): void => {
      if (!isMultiSelectOn) {
        return;
      }

      const { blobGroupIndex, blobIndex, tokenIndex } = location;
      setMultiSelectTokenIndeces((prev) =>
        prev.map((prevBlobGroup, prevBlobGroupIndex) => {
          if (prevBlobGroupIndex === blobGroupIndex) {
            return prevBlobGroup.map((prevBlob, prevBlobIndex) =>
              prevBlobIndex === blobIndex
                ? toggleInList(prevBlob, tokenIndex)
                : prevBlob
            );
          }
          return prevBlobGroup;
        })
      );
    },
    [isMultiSelectOn]
  );

  const removeWordFromMultiSelect = (wordId: number): void => {
    setMultiSelectTokenIndeces((prev) =>
      prev.map((prevBlobGroup, blobGroupIndex) =>
        prevBlobGroup.map((prevBlob, blobIndex) =>
          prevBlob.filter((tokenIndex) => {
            const token: Token =
              blobGroups[blobGroupIndex][blobIndex][tokenIndex];
            if (isWordToken(token)) {
              const word: WordView = token.word!;
              return word.id !== wordId;
            }
            return false;
          })
        )
      )
    );
  };

  //SNIPPET
  //const dedupedItems: string[] = [...new Set(items)];
  const multiSelectedWords: WordView[] = useMemo(() => {
    //SNIPPET
    //const uniqueObjectsMap = new Map<number, MyObject>();
    const wordsMap = new Map<number, WordView>();
    multiSelectTokenIndeces.forEach((blobGroup, blobGroupIndex) => {
      blobGroup.forEach((blobTokens, blobIndex) => {
        blobTokens.forEach((tokenIndex) => {
          const token: Token =
            blobGroups[blobGroupIndex][blobIndex][tokenIndex];
          if (!isWordToken(token)) {
            // TODO: this will happen if you click on a token that's not a word token
            // maybe the Blob/Token callbacks should be for words only...?
            //throw Error("this shouldn't happen?");
          } else {
            const word: WordView = token.word!;
            wordsMap.set(word.id, word);
          }
        });
      });
    });

    return Array.from(wordsMap.values());
  }, [multiSelectTokenIndeces]);

  return {
    multiSelectTokenIndeces,
    multiSelectedWords,
    isMultiSelectOn,
    setIsMultiSelectOn,
    closeMultiSelect,
    handleToggleMultiSelect,
    removeWordFromMultiSelect,
  };
}

export default useMultiSelect;

function toggleInList<T>(original: T[], newb: T): T[] {
  const rv = original.includes(newb)
    ? original.filter((x) => x !== newb)
    : [...original, newb];
  rv.sort();
  return rv;
}
